import { useEffect, useRef } from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { connectRelay, toPlainText, type Conversation, type SlackEvent, type SlackMessage, type SlackUser } from '@assembly/slack'
import { usePlatform, type Platform } from '../platform'
import { useUi } from '../state/ui'
import type { Account } from '../state/accounts'
import { useWorkspace } from './workspace'
import { keys } from './keys'
import { applyReaction, reconcileLatest, removeMessage, updateMessage, upsertMessage } from './cache'
import { displayName } from './hooks'

const VISIBLE_SUBTYPES = new Set([undefined, 'thread_broadcast', 'bot_message', 'me_message', 'file_share', 'channel_join', 'channel_leave', 'channel_topic', 'channel_purpose', 'channel_name', 'channel_archive', 'channel_unarchive', 'pinned_item'])

/**
 * When the relay last delivered an event (ms). A connected relay that has gone quiet
 * may not be receiving events at all (e.g. the Slack app isn't subscribed to them),
 * so polling continues until events are actually flowing.
 */
export let lastRelayEventAt = 0
export const relayQuiet = (ms: number) => Date.now() - lastRelayEventAt > ms

const channelOf = (e: SlackEvent): string | undefined => (typeof e.channel === 'string' ? e.channel : e.channel?.id)

function handleEvent(e: SlackEvent, qc: QueryClient, account: Account, platform: Platform) {
  const a = account.id
  const channel = channelOf(e)

  switch (e.type) {
    case 'message': {
      if (!channel) return
      if (e.subtype === 'message_changed' && e.message) {
        updateMessage(qc, a, channel, e.message.ts, (m) => ({ ...m, ...e.message }))
        return
      }
      if (e.subtype === 'message_deleted' && e.deleted_ts) {
        removeMessage(qc, a, channel, e.deleted_ts)
        return
      }
      if (e.subtype === 'message_replied' && e.message) {
        updateMessage(qc, a, channel, e.message.ts, (m) => ({ ...m, ...e.message }))
        return
      }
      if (!VISIBLE_SUBTYPES.has(e.subtype)) return
      const msg = e as unknown as SlackMessage
      upsertMessage(qc, a, channel, { ...msg, channel: undefined, pending: false })
      useUi.getState().noteMessage(channel, msg.ts)
      if (e.subtype?.startsWith('channel_')) qc.invalidateQueries({ queryKey: keys.conversation(a, channel) })

      // Unread + notifications for messages from others.
      if (msg.user === account.userId) return
      const ui = useUi.getState()
      const conv = qc.getQueryData<Conversation[]>(keys.conversations(a))?.find((c) => c.id === channel)
      const isDirect = !!(conv?.is_im || conv?.is_mpim)
      const mentioned = msg.text?.includes(`<@${account.userId}>`) || /<!(here|channel|everyone)>/.test(msg.text ?? '')
      const isReply = !!msg.thread_ts && msg.thread_ts !== msg.ts && msg.subtype !== 'thread_broadcast'
      const viewing = ui.channelId === channel && ui.view.type === 'channel' && document.visibilityState === 'visible'
      if (!isReply && !viewing) ui.bumpUnread(channel, mentioned || isDirect)
      if ((mentioned || isDirect) && (!viewing || !document.hasFocus())) {
        const users = qc.getQueryData<Map<string, SlackUser>>(keys.users(a))
        const name = (id: string) => displayName(users?.get(id), id)
        const from = msg.user ? name(msg.user) : (msg.username ?? msg.bot_profile?.name ?? 'Someone')
        const where = conv?.is_im ? from : conv?.name ? `${from} in #${conv.name}` : from
        platform.notify?.(where, toPlainText(msg.text ?? '', { user: name }), () => {
          window.focus()
          useUi.getState().openChannel(channel, { highlight: msg.ts })
        })
      }
      return
    }

    case 'reaction_added':
    case 'reaction_removed': {
      if (e.item?.type !== 'message' || !e.reaction) return
      const user = typeof e.user === 'string' ? e.user : e.user?.id
      if (!user) return
      updateMessage(qc, a, e.item.channel, e.item.ts, (m) => applyReaction(m, e.reaction!, user, e.type === 'reaction_added'))
      return
    }

    case 'pin_added':
    case 'pin_removed': {
      if (!channel) return
      const ts = (e.item as any)?.message?.ts as string | undefined
      if (ts) {
        updateMessage(qc, a, channel, ts, (m) => ({
          ...m,
          pinned_to: e.type === 'pin_added' ? [...new Set([...(m.pinned_to ?? []), channel])] : (m.pinned_to ?? []).filter((c) => c !== channel),
        }))
      }
      qc.invalidateQueries({ queryKey: keys.pins(a, channel) })
      return
    }

    case 'member_joined_channel':
    case 'member_left_channel':
      if (channel) qc.invalidateQueries({ queryKey: keys.members(a, channel) })
      if (e.user === account.userId) qc.invalidateQueries({ queryKey: keys.conversations(a) })
      if (channel) qc.invalidateQueries({ queryKey: keys.conversation(a, channel) })
      return

    case 'channel_created':
      qc.invalidateQueries({ queryKey: ['publicChannels', a] })
      return

    case 'channel_rename':
    case 'group_rename':
    case 'channel_archive':
    case 'channel_unarchive':
    case 'group_archive':
    case 'group_unarchive':
    case 'channel_deleted':
      qc.invalidateQueries({ queryKey: keys.conversations(a) })
      qc.invalidateQueries({ queryKey: ['publicChannels', a] })
      if (channel) qc.invalidateQueries({ queryKey: keys.conversation(a, channel) })
      return

    case 'user_change': {
      const user = e.user as unknown as SlackUser
      if (!user?.id) return
      qc.setQueryData<Map<string, SlackUser>>(keys.users(a), (users) => {
        if (!users) return users
        const next = new Map(users)
        next.set(user.id, user)
        return next
      })
      return
    }

    case 'emoji_changed':
      qc.invalidateQueries({ queryKey: keys.emoji(a) })
      return
  }
}

/**
 * Keeps the cache live: subscribes to the relay when available, otherwise polls the
 * open conversation and thread. Also marks the open conversation as read.
 */
export function useRealtime() {
  const qc = useQueryClient()
  const platform = usePlatform()
  const { account, client } = useWorkspace()
  const realtime = useUi((s) => s.realtime)
  const setRealtime = useUi((s) => s.setRealtime)
  const accountRef = useRef(account)
  accountRef.current = account

  useEffect(() => {
    if (!platform.relayUrl) {
      setRealtime('polling')
      return
    }
    setRealtime('connecting')
    let everLive = false
    const fallback = setTimeout(() => !everLive && setRealtime('polling'), 8000)
    const conn = connectRelay(platform.relayUrl, account.token, {
      onEvent: (event) => {
        lastRelayEventAt = Date.now()
        handleEvent(event, qc, accountRef.current, platform)
      },
      onStatus: (live) => {
        if (live) everLive = true
        setRealtime(live ? 'live' : everLive ? 'connecting' : 'polling')
      },
      onFatal: () => setRealtime('polling'),
    })
    return () => {
      clearTimeout(fallback)
      conn.close()
    }
  }, [platform, account.token, qc, setRealtime])

  // Polling fallback for the open conversation and thread.
  const channelId = useUi((s) => s.channelId)
  const panel = useUi((s) => s.panel)
  useEffect(() => {
    if (!channelId) return
    let stopped = false
    const tick = async () => {
      if (document.visibilityState !== 'visible') return
      // Live and events are arriving: nothing to poll for.
      if (realtime === 'live' && !relayQuiet(90_000)) return
      try {
        const res = await client.conversations.history(channelId, { limit: 30 })
        if (!stopped) reconcileLatest(qc, account.id, channelId, res.messages)
        if (panel?.type === 'thread') qc.invalidateQueries({ queryKey: keys.replies(account.id, panel.channel, panel.ts) })
      } catch {
        /* transient */
      }
    }
    const id = setInterval(tick, 6000)
    return () => {
      stopped = true
      clearInterval(id)
    }
  }, [realtime, channelId, panel, client, qc, account.id])

  // When reconnecting after a drop, refresh whatever is open.
  useEffect(() => {
    if (realtime === 'live' && channelId) qc.invalidateQueries({ queryKey: keys.history(account.id, channelId) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realtime])

  // Dock / app badge = total unread mentions + DMs.
  const mentions = useUi((s) => s.mentions)
  useEffect(() => {
    platform.setBadge?.(Object.values(mentions).reduce((sum, n) => sum + n, 0))
  }, [mentions, platform])
}
